/**
 * ReviewsHero — Weidner Via6 page hero for /reviews.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Reviews hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function ReviewsHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="reviewshero.title"
        bgPath="reviewshero.bg_image"
        bgDef={bgDef}
        defaultTitle="Reviews"
        keywords={['lifestyle', 'lounge', 'residents', 'community']}
        pageHint="reviewshero"
        altText="Reviews hero image"
      />

      <RichTextContent
        path="reviewshero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="reviewshero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsHero',
  displayName: 'Reviews Hero',
  tokens: {
    'reviewshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Reviews',
    },
  },
  slotGroups: {
    'reviewshero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Reviews hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(ReviewsHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewsHero;
